/* ══════════════════════════════════════════════════════════════
   <product-grid> — sections/product-grid.liquid
   Sort, filters, sticky toolbar, quick add, image arrows/swipe,
   image fade-in, deferred hover images and AJAX pagination.
   Settings arrive as data-* attributes on the element.
   ══════════════════════════════════════════════════════════════ */
(function () {
  if (customElements.get('product-grid')) return;

  /* ── Image fade-in (.pc-loaded) ── */
  function initCards(root) {
    (root || document).querySelectorAll('.product-card__img:not(.pc-init):not([data-pg-defer])').forEach(function (img) {
      img.classList.add('pc-init');
      if (img.complete) {
        img.classList.add('pc-loaded');
      } else {
        img.addEventListener('load', function () { img.classList.add('pc-loaded'); }, { once: true });
        img.addEventListener('error', function () { img.classList.add('pc-loaded'); }, { once: true });
      }
    });
  }
  document.addEventListener('shopify:section:load', function (e) { initCards(e.target); });
  document.addEventListener('pg:cards-updated', function (e) { initCards(e.detail && e.detail.container); });
  initCards();

  /* ── Deferred hover / extra images ──
     Cards render their 2nd+ images with data-src/data-srcset. They are
     released once the page has loaded and the browser is idle; hovering,
     touching or focusing a card releases that card straight away. */
  var pageReleased = false;
  function releaseImg(img, eager) {
    if (!img.hasAttribute('data-pg-defer')) return;
    if (eager) img.loading = 'eager';
    if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
    if (img.dataset.src) img.setAttribute('src', img.dataset.src);
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
    img.removeAttribute('data-pg-defer');
    initCards(img.parentNode);
  }
  function releaseAll(root, eager) {
    (root || document).querySelectorAll('img[data-pg-defer]').forEach(function (img) { releaseImg(img, eager); });
  }
  function scheduleRelease() {
    setTimeout(function () {
      var go = function () { pageReleased = true; releaseAll(); };
      if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 2000 });
      else go();
    }, 600);
  }
  if (document.readyState === 'complete') scheduleRelease();
  else window.addEventListener('load', scheduleRelease, { once: true });

  /* ── Cart quantities, shared by every grid on the page ── */
  var cartQty = null;
  var cartRequest = null;
  function loadCart() {
    return fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartQty = {};
        cart.items.forEach(function (item) { cartQty[String(item.variant_id)] = item.quantity; });
        return cart;
      });
  }
  function refreshCart() {
    if (!cartRequest) {
      cartRequest = loadCart()
        .catch(function () { cartQty = cartQty || {}; return null; })
        .then(function (cart) { cartRequest = null; return cart; });
    }
    return cartRequest;
  }
  function updateHeaderCount(cart) {
    if (!cart) return;
    var countEl = document.querySelector('.header__cart-count');
    if (countEl) {
      countEl.textContent = cart.item_count;
      countEl.style.display = cart.item_count > 0 ? '' : 'none';
    }
  }
  document.addEventListener('cart:updated', function (e) {
    if (e.detail && e.detail.source === 'product-grid') return;
    if (cartQty) refreshCart();
  });

  /* ── Sticky header offset (for the sticky toolbar) ── */
  var headerEls = null;
  function findHeaderEls() {
    headerEls = [];
    ['.shopify-section:has(.site-header--sticky)', '.shopify-section:has(.site-header--smart)'].forEach(function (sel) {
      var el; try { el = document.querySelector(sel); } catch (e) { return; }
      if (el) headerEls.push(el);
    });
  }
  document.addEventListener('shopify:section:load', function () { headerEls = null; });
  function getStickyHeaderBottom() {
    if (!headerEls) findHeaderEls();
    var offset = 0;
    headerEls.forEach(function (el) {
      // Skip if smart header is currently hidden (slid away)
      var hdr = el.querySelector('.site-header--smart');
      if (hdr && hdr.classList.contains('is-hidden')) return;
      var pos = getComputedStyle(el).position;
      if (pos === 'fixed' || pos === 'sticky') {
        var bottom = el.getBoundingClientRect().bottom;
        if (bottom > 0) offset = Math.max(offset, bottom);
      }
    });
    // Countdown bar: sticky lives on its .shopify-section wrapper
    var ctEl = document.querySelector('[data-countdown-bar]');
    if (ctEl && ctEl.style.display !== 'none') {
      var ctSection = ctEl.closest('.shopify-section') || ctEl;
      var ctPos = getComputedStyle(ctSection).position;
      if (ctPos === 'fixed' || ctPos === 'sticky') {
        var b = ctEl.getBoundingClientRect().bottom;
        if (b > 0) offset = Math.max(offset, b);
      }
    }
    return Math.round(offset);
  }

  class ProductGrid extends HTMLElement {
    connectedCallback() {
      this.sid = this.dataset.sectionId;
      this.ac = new AbortController();
      var signal = this.ac.signal;

      this.addEventListener('click', this.onClick.bind(this), { signal: signal });
      this.addEventListener('change', this.onChange.bind(this), { signal: signal });
      document.addEventListener('click', this.onDocClick.bind(this), { signal: signal });
      document.addEventListener('keydown', this.onKeydown.bind(this), { signal: signal });
      window.addEventListener('popstate', this.onPopstate.bind(this), { signal: signal });

      if (this.hasAttribute('data-nav-swipe')) {
        this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true, signal: signal });
        this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true, signal: signal });
      }

      ['pointerenter', 'touchstart', 'focusin'].forEach(function (type) {
        this.addEventListener(type, this.onCardIntent.bind(this), { capture: true, passive: true, signal: signal });
      }, this);

      if (this.hasAttribute('data-qa') && cartQty === null) {
        var warm = function () { if (cartQty === null) refreshCart(); };
        if (document.readyState === 'complete') setTimeout(warm, 0);
        else window.addEventListener('load', warm, { once: true, signal: signal });
      }

      initCards(this);
      this.initArrows();
      this.initSticky();
    }

    disconnectedCallback() {
      this.ac.abort();
      if (this.stickyCleanup) this.stickyCleanup();
    }

    get container() { return this.querySelector('[data-pg-container]'); }

    /* Hover/touch/focus on a card: load its extra images now. They sit
       off to the side of the card, so lazy loading alone may wait. */
    onCardIntent(e) {
      var card = e.target && e.target.closest && e.target.closest('.product-card');
      if (!card || card.dataset.pgIntent) return;
      card.dataset.pgIntent = '1';
      releaseAll(card, true);
      card.querySelectorAll('.product-card__img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
    }

    /* ══ Click handling inside the section ══ */
    onClick(e) {
      var t = e.target;

      /* Sort button */
      var sortBtn = t.closest('.pg-sort-btn');
      if (sortBtn) {
        var panel = sortBtn.nextElementSibling;
        var isOpen = sortBtn.getAttribute('aria-expanded') === 'true';
        sortBtn.setAttribute('aria-expanded', String(!isOpen));
        panel.hidden = isOpen;
        return;
      }
      /* Sort option: reorders the cards on this page by price */
      var sortOpt = t.closest('.pg-sort-option');
      if (sortOpt) { this.sortBy(sortOpt); return; }

      /* Filter drawer */
      if (t.closest('.pg-filter-btn')) { this.openDrawer(); return; }
      if (t.closest('.pg-filter-drawer__close') || t.classList.contains('pg-filter-overlay')) { this.closeDrawer(); return; }
      var filterToggle = t.closest('[data-filter-toggle]');
      if (filterToggle) { this.toggleFilterSection(filterToggle); return; }
      if (t.closest('.pg-filter-reset')) {
        this.querySelectorAll('.pg-filter-drawer input[type="checkbox"]').forEach(function (cb) { cb.checked = false; });
        this.applyFilters();
        this.updateFilterCounts();
        return;
      }
      if (t.closest('.pg-filter-apply')) { this.applyFilters(); this.closeDrawer(); return; }

      /* Image navigation arrows */
      var navBtn = this.hasAttribute('data-nav-arrows') && t.closest('[data-nav-prev],[data-nav-next]');
      if (navBtn) {
        var wrapper = navBtn.closest('[data-img-nav]');
        if (wrapper) {
          e.preventDefault();
          this.goTo(wrapper, this.getIdx(wrapper) + (navBtn.hasAttribute('data-nav-prev') ? -1 : 1));
        }
        return;
      }

      /* Quick add */
      if (this.hasAttribute('data-qa') && this.onQuickAddClick(e)) return;

      /* AJAX pagination */
      var pagLink = t.closest('.pg-pagination__page, .pg-pagination__btn, .pg-pagination__load-btn');
      if (pagLink && this.container && this.container.contains(pagLink)) {
        var href = pagLink.getAttribute('href');
        if (!href) return;
        e.preventDefault();
        this.loadPage(href, true);
      }
    }

    /* Clicks anywhere on the page: close this section's sort panel and open quick-add panels */
    onDocClick(e) {
      var t = e.target;
      if (!t.closest || !(this.contains(t) && t.closest('.pg-sort-btn, .pg-sort-option'))) {
        this.querySelectorAll('.pg-sort-panel').forEach(function (p) { p.hidden = true; });
        this.querySelectorAll('.pg-sort-btn').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      }
      if (!this.hasAttribute('data-qa') || !t.closest) return;
      if (t.closest('[data-qa-back]')) return;
      var pill = t.closest('.product-card__qa-pill');
      if (pill && this.contains(pill)) return;
      var icon = t.closest('[data-qa-icon]');
      if (icon && icon.dataset.qaSection === this.sid && window.innerWidth < 768) return;
      if (!t.closest('.product-card__image-wrapper')) this.closeQaPanels();
    }

    onChange(e) {
      if (e.target && e.target.dataset && e.target.dataset.filterType) this.updateFilterCounts();
    }

    onKeydown(e) {
      if (e.key !== 'Escape') return;
      var drawer = this.querySelector('.pg-filter-drawer[aria-hidden="false"]');
      if (drawer) this.closeDrawer();
    }

    /* ══ Sort & filter (current page only) ══ */
    sortBy(sortOpt) {
      var grid = this.querySelector('.product-grid');
      if (!grid) return;
      var sortValue = sortOpt.dataset.sortValue;
      sortOpt.closest('.pg-sort-panel').hidden = true;
      var sb = this.querySelector('.pg-sort-btn');
      if (sb) sb.setAttribute('aria-expanded', 'false');
      this.querySelectorAll('.pg-sort-option').forEach(function (o) {
        o.classList.toggle('pg-sort-option--active', o === sortOpt);
      });
      var cards = Array.from(grid.querySelectorAll('.product-card'));
      cards.sort(function (a, b) {
        var pa = parseInt(a.dataset.price || '0', 10);
        var pb = parseInt(b.dataset.price || '0', 10);
        return sortValue === 'price-ascending' ? pa - pb : pb - pa;
      });
      grid.style.opacity = '0.4';
      grid.style.transition = 'opacity 0.2s';
      setTimeout(function () {
        cards.forEach(function (c, i) { c.style.order = i + 1; });
        grid.style.opacity = '1';
      }, 200);
    }

    openDrawer() {
      var drawer = document.getElementById('pg-filter-drawer-' + this.sid);
      var overlay = document.getElementById('pg-filter-overlay-' + this.sid);
      if (!drawer) return;
      overlay.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('drawer-is-open');
    }

    closeDrawer() {
      var drawer = document.getElementById('pg-filter-drawer-' + this.sid);
      var overlay = document.getElementById('pg-filter-overlay-' + this.sid);
      if (!drawer) return;
      drawer.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.hidden = true;
      document.body.style.overflow = '';
      document.body.classList.remove('drawer-is-open');
    }

    toggleFilterSection(header) {
      var body = header.nextElementSibling;
      var isOpen = header.classList.contains('pg-filter-section__header--open');
      var drawer = header.closest('.pg-filter-drawer');
      if (!drawer || !body) return;
      drawer.querySelectorAll('.pg-filter-section__header').forEach(function (h) {
        h.classList.remove('pg-filter-section__header--open');
        if (h.nextElementSibling) h.nextElementSibling.classList.remove('is-open');
      });
      if (!isOpen) {
        header.classList.add('pg-filter-section__header--open');
        body.classList.add('is-open');
      }
    }

    applyFilters() {
      var grid = this.querySelector('.product-grid');
      if (!grid) return;
      var checked = function (type) {
        return Array.from(this.querySelectorAll('input[data-filter-type="' + type + '"]:checked')).map(function (cb) { return cb.value; });
      }.bind(this);
      var availability = this.querySelector('input[data-filter-type="availability"]:checked');
      var colors = checked('color'), sizes = checked('size'), categories = checked('category');
      grid.querySelectorAll('.product-card').forEach(function (card) {
        var show = true;
        if (availability && card.dataset.available !== 'true') show = false;
        if (colors.length > 0) {
          var cardColors = card.dataset.colors || '';
          if (!colors.some(function (c) { return cardColors.indexOf(c) !== -1; })) show = false;
        }
        if (sizes.length > 0) {
          var cardSizes = card.dataset.sizes || '';
          if (!sizes.some(function (s) { return cardSizes.indexOf(s) !== -1; })) show = false;
        }
        if (categories.length > 0) {
          var cardType = card.dataset.type || '';
          if (!categories.some(function (c) { return cardType === c; })) show = false;
        }
        card.style.display = show ? '' : 'none';
      });
    }

    updateFilterCounts() {
      var self = this;
      ['availability', 'color', 'size', 'category'].forEach(function (type) {
        var badge = self.querySelector('[data-filter-count="' + type + '"]');
        if (!badge) return;
        var n = self.querySelectorAll('input[data-filter-type="' + type + '"]:checked').length;
        badge.textContent = n;
        badge.classList.toggle('is-active', n > 0);
      });
      var totalBadge = document.getElementById('pg-filter-total-' + this.sid);
      if (totalBadge) {
        var total = this.querySelectorAll('input[data-filter-type]:checked').length;
        totalBadge.textContent = total;
        totalBadge.classList.toggle('is-active', total > 0);
      }
    }

    /* ══ Sticky toolbar ══ */
    initSticky() {
      var toolbar = this.querySelector('.pg-toolbar[data-sticky="true"]');
      if (!toolbar) return;
      var spacer = document.getElementById('pg-toolbar-spacer-' + this.sid);
      if (!spacer) return;
      var sectionEl = this.closest('[id^="shopify-section-"]') || this;
      var toolbarHeight = toolbar.offsetHeight;
      var isFixed = false;
      var ticking = false;
      var toolbarTop = 0;
      spacer.style.height = toolbarHeight + 'px';

      function captureToolbarTop() {
        // Only re-read when NOT already fixed (so layout is in natural flow)
        if (!isFixed) toolbarTop = toolbar.getBoundingClientRect().top + window.scrollY;
      }
      function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          var scrollY = window.scrollY;
          var stickyOffset = getStickyHeaderBottom();
          var pastSection = sectionEl.getBoundingClientRect().bottom <= stickyOffset + toolbarHeight;
          if (!pastSection && scrollY + stickyOffset >= toolbarTop && !isFixed) {
            isFixed = true; toolbar.classList.add('pg-toolbar--sticky'); toolbar.style.top = stickyOffset + 'px'; spacer.style.display = 'block';
          } else if ((scrollY + stickyOffset < toolbarTop || pastSection) && isFixed) {
            isFixed = false; toolbar.classList.remove('pg-toolbar--sticky'); toolbar.style.top = ''; spacer.style.display = 'none';
          } else if (isFixed) {
            toolbar.style.top = stickyOffset + 'px';
          }
          ticking = false;
        });
      }
      function onLoad() { if (!isFixed) captureToolbarTop(); }

      requestAnimationFrame(function () { captureToolbarTop(); onScroll(); });
      window.addEventListener('load', onLoad);
      window.addEventListener('scroll', onScroll, { passive: true });
      this.stickyCleanup = function () {
        window.removeEventListener('load', onLoad);
        window.removeEventListener('scroll', onScroll);
      };
    }

    /* ══ Quick add ══ */
    closeQaPanels() {
      this.querySelectorAll('.product-card__image-wrapper.qa-is-open').forEach(function (w) {
        w.classList.remove('qa-is-open');
        var btn = w.closest('.product-card').querySelector('[data-qa-icon]');
        if (btn) { btn.classList.remove('qa-icon--open'); btn.setAttribute('aria-expanded', 'false'); }
      });
    }

    /* Returns true when the click was a quick-add interaction */
    onQuickAddClick(e) {
      var t = e.target;
      var backBtn = t.closest('[data-qa-back]');
      if (backBtn) {
        var backBar = backBtn.closest('.product-card__qa-bar');
        if (backBar && backBar.dataset.qaSection === this.sid) {
          e.preventDefault();
          backBar.querySelectorAll('.pg-qa-step').forEach(function (s) { s.classList.remove('pg-qa-step--active'); });
          backBar.querySelector('[data-qa-step="0"]').classList.add('pg-qa-step--active');
        }
        return true;
      }

      var pill = t.closest('.product-card__qa-pill');
      if (!pill) {
        var iconBtn = t.closest('[data-qa-icon]');
        if (iconBtn && iconBtn.dataset.qaSection === this.sid && window.innerWidth < 768) {
          e.preventDefault();
          var wrapper = iconBtn.closest('.product-card').querySelector('.product-card__image-wrapper');
          var wasOpen = wrapper.classList.contains('qa-is-open');
          this.closeQaPanels();
          if (!wasOpen) {
            wrapper.classList.add('qa-is-open');
            iconBtn.classList.add('qa-icon--open');
            iconBtn.setAttribute('aria-expanded', 'true');
          }
          return true;
        }
        return false;
      }

      /* stop the parent <a> from navigating */
      e.preventDefault();

      /* Multi-step: step-0 pill advances to step 1 */
      var bar = pill.closest('[data-qa-multi]');
      if (bar && bar.dataset.qaSection === this.sid && pill.dataset.stepValue !== undefined) {
        bar.querySelectorAll('.pg-qa-step').forEach(function (s) { s.classList.remove('pg-qa-step--active'); });
        var next = bar.querySelector('[data-qa-step="1"][data-qa-for="' + pill.dataset.stepValue + '"]');
        if (next) next.classList.add('pg-qa-step--active');
        return true;
      }

      if (pill.dataset.available === 'false') {
        this.showToast(this.dataset.msgSoldout, true);
        return true;
      }
      var vid = pill.dataset.variantId;
      if (!vid) return true;

      var self = this;
      var check = cartQty ? Promise.resolve() : refreshCart();
      check.then(function () { self.checkStockAndAdd(pill, vid); });
      return true;
    }

    checkStockAndAdd(pill, vid) {
      // Inventory: use the pill's own data-inventory if present, else the card's variant map
      var invQty, invPolicy;
      if (pill.dataset.inventory !== undefined && pill.dataset.inventory !== '') {
        invQty = parseInt(pill.dataset.inventory, 10);
        invPolicy = pill.dataset.inventoryPolicy || 'deny';
      } else {
        var card = pill.closest('.product-card');
        var invMap = {};
        (card ? card.dataset.variantInv || '' : '').split('|').forEach(function (entry) {
          if (!entry) return;
          var parts = entry.split(':');
          if (parts.length >= 3) invMap[parts[0]] = { qty: parseInt(parts[1], 10), policy: parts[2] };
        });
        var invData = invMap[vid] || {};
        invQty = invData.qty != null ? invData.qty : NaN;
        invPolicy = invData.policy || 'deny';
      }
      var inCart = (cartQty && cartQty[vid]) || 0;
      if (invPolicy === 'deny' && !isNaN(invQty) && invQty > 0 && inCart >= invQty) {
        this.showToast(this.dataset.msgStock.replace('%available%', invQty).replace('%in_cart%', inCart), true);
        return;
      }
      this.addToCart(vid, pill);
    }

    addToCart(variantId, pill) {
      var self = this;
      pill.classList.add('is-adding');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
        .then(function (res) {
          pill.classList.remove('is-adding');
          if (res.ok) {
            cartQty[String(variantId)] = (cartQty[String(variantId)] || 0) + 1;
            loadCart().then(updateHeaderCount).catch(function () {});
            self.showToast(self.dataset.msgAdded);
            document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { item: res.d } }));
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'product-grid' } }));
          } else {
            refreshCart();
            self.showToast((res.d && (res.d.description || res.d.message)) || 'Error adding to cart.', true);
          }
        })
        .catch(function () {
          pill.classList.remove('is-adding');
          refreshCart();
          self.showToast('Error adding to cart.', true);
        });
    }

    showToast(msg, isErr) {
      if (!isErr && this.hasAttribute('data-notify-panel')) return;
      var toast = document.getElementById('pg-qa-toast-' + this.sid);
      if (!toast) return;
      clearTimeout(this.toastTimer);
      toast.querySelector('.pg-qa-toast__msg').textContent = msg;
      toast.classList.toggle('is-error', !!isErr);
      toast.querySelector('.pg-qa-toast__view').style.display = isErr ? 'none' : '';
      toast.classList.add('is-visible');
      this.toastTimer = setTimeout(function () { toast.classList.remove('is-visible'); }, 3200);
    }

    /* ══ Image navigation: arrows + swipe ══ */
    getCount(wrapper) {
      var track = wrapper.querySelector('[data-card-track]');
      if (track) {
        var n = track.querySelectorAll('.product-card__img').length;
        if (n > 0) return n;
      }
      return parseInt(wrapper.getAttribute('data-img-count'), 10) || 0;
    }

    getIdx(wrapper) {
      return parseInt(wrapper.getAttribute('data-nav-idx') || '0', 10);
    }

    updateArrows(wrapper) {
      var idx = this.getIdx(wrapper), count = this.getCount(wrapper);
      var prev = wrapper.querySelector('[data-nav-prev]');
      var next = wrapper.querySelector('[data-nav-next]');
      if (prev) prev.style.visibility = idx <= 0 ? 'hidden' : 'visible';
      if (next) next.style.visibility = idx >= count - 1 ? 'hidden' : 'visible';
    }

    initArrows() {
      if (!this.hasAttribute('data-nav-arrows')) return;
      this.querySelectorAll('[data-img-nav]').forEach(this.updateArrows, this);
    }

    goTo(wrapper, idx) {
      var count = this.getCount(wrapper);
      if (count < 2) return;
      idx = Math.max(0, Math.min(idx, count - 1));
      releaseAll(wrapper, true);
      wrapper.querySelectorAll('.product-card__img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
      wrapper.setAttribute('data-nav-idx', idx);
      var track = wrapper.querySelector('[data-card-track]');
      if (track) track.style.transform = 'translateX(-' + (idx * 100) + '%)';
      this.updateArrows(wrapper);
    }

    onTouchStart(e) {
      var wrapper = e.target.closest('[data-img-nav]');
      if (!wrapper) return;
      this.touchStartX = e.touches[0].clientX;
    }

    onTouchEnd(e) {
      if (this.touchStartX == null) return;
      var pt = e.changedTouches && e.changedTouches[0];
      var wrapper = e.target.closest('[data-img-nav]');
      if (!wrapper && pt) {
        var under = document.elementFromPoint(pt.clientX, pt.clientY);
        wrapper = under && under.closest('[data-img-nav]');
      }
      if (!wrapper || !this.contains(wrapper)) { this.touchStartX = null; return; }
      var dx = pt.clientX - this.touchStartX;
      this.touchStartX = null;
      if (Math.abs(dx) < 30) return;
      this.goTo(wrapper, this.getIdx(wrapper) + (dx < 0 ? 1 : -1));
    }

    /* ══ AJAX pagination (fetches only this section) ══ */
    onPopstate(e) {
      this.loadPage((e.state && e.state.pgUrl) ? e.state.pgUrl : window.location.href, false);
    }

    loadPage(url, push) {
      var container = this.container;
      if (!container) return;
      var self = this;
      container.style.opacity = '0.4';
      container.style.pointerEvents = 'none';
      container.style.transition = 'opacity 0.15s';

      var sectionUrl = new URL(url, window.location.origin);
      sectionUrl.searchParams.set('section_id', this.sid);

      fetch(sectionUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) {
          if (!r.ok) throw new Error('Network error ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.getElementById('pg-grid-container-' + self.sid);
          if (!fresh) { window.location.href = url; return; }
          container.innerHTML = fresh.innerHTML;
          container.style.opacity = '';
          container.style.pointerEvents = '';

          if (pageReleased) releaseAll(container);
          initCards(container);
          self.initArrows();
          document.dispatchEvent(new CustomEvent('pg:cards-updated', { detail: { container: container } }));

          if (push) history.pushState({ pgUrl: url }, '', url);

          var top = container.getBoundingClientRect().top + window.scrollY;
          var headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--pg-header-h') || '0', 10) || 0;
          window.scrollTo({ top: Math.max(0, top - headerH - 24), behavior: 'smooth' });
        })
        .catch(function () {
          container.style.opacity = '';
          container.style.pointerEvents = '';
          window.location.href = url;
        });
    }
  }

  customElements.define('product-grid', ProductGrid);
})();
